import { createReducer } from '@reduxjs/toolkit';

import createMiddleware from 'common/apps/core/createMiddleware';
import { init } from 'common/apps/core/createApp';
import { modalsComponents } from 'common/components/ModalsContainer';
import { MODAL_TYPES } from 'common/constants/common';
import SurveyQuestionsModal from 'common/components/SurveyQuestions/components/SurveyQuestionsModal';
import { error } from 'common/apps/modules/notifications/events';

import { showModalAction } from '../modals/events';

import {
  setActiveStep,
  setLoadingSurvey,
  setSurveyData,
  setSurveyAnswer,
  setSurveySubmitted,
} from './events';
import defaultState from './constants';
import { Survey, SurveyButtonType } from './types';
import surveyModuleSelectors from './selectors/surveyModuleSelectors';
import rootSurveySelector from './selectors/rootSurveySelector';
import getSurveyDataService from './services/getSurveyDataService/getSurveyDataService';
import postSurveyDataService from './services/postSurveyDataService';
import onMergeAnswer from './utils/onMergeAnswer';

const surveyModule: Survey.Module = () => ({
  name: 'survey',
  reducer: createReducer(defaultState, (builder) =>
    builder
      .addCase(setSurveyData, (state, { payload: surveyData }) => ({
        ...state,
        surveyData,
      }))
      .addCase(setSurveyAnswer, (state, { payload }) =>
        onMergeAnswer(state, payload),
      )
      .addCase(setLoadingSurvey, (state, { payload: isLoading }) => ({
        ...state,
        isLoading,
      }))
      .addCase(setSurveySubmitted, (state) => ({
        ...state,
        isSurveySubmitted: true,
      }))
      .addCase(setActiveStep, (state, { payload: activeStep }) => ({
        ...state,
        activeStep,
      })),
  ),

  middleware: [
    createMiddleware(init, (_, { dispatch }) => {
      modalsComponents.injectModals({
        [MODAL_TYPES.SURVEY_QUESTIONS]: SurveyQuestionsModal,
      });

      getSurveyDataService().then((surveyData) =>
        dispatch(setSurveyData(surveyData)),
      );
    }),
  ],
  api: {
    handleShowModal: (_, { dispatch }) => {
      dispatch(showModalAction({ modalName: MODAL_TYPES.SURVEY_QUESTIONS }));
    },
    handleAnswer: ({ id, answer }, { dispatch, getState }) => {
      const surveyData = surveyModuleSelectors.surveyDataSelector(getState());

      if (surveyData === null) return;

      dispatch(setSurveyAnswer({ id, answer }));
    },
    handleChangeStep: (event, { dispatch, getState, broadcast }) => {
      const { surveyData, activeStep } = rootSurveySelector(getState());

      if (surveyData === null) return;

      const type = event.currentTarget.id;

      if (
        type === SurveyButtonType.Next &&
        activeStep === surveyData.questions.length
      ) {
        dispatch(setLoadingSurvey(true));

        postSurveyDataService({
          id: surveyData.id,
          body: {
            results: surveyData.questions.map(({ uuid, answer = '' }) => ({
              questionUuid: uuid,
              userAnswer: answer,
            })),
          },
        })
          .then(() => dispatch(setSurveySubmitted()))
          .catch(({ error: errorMessage }) => broadcast(error(errorMessage)))
          .finally(() => dispatch(setLoadingSurvey(false)));
      }

      if (
        type === SurveyButtonType.Next &&
        activeStep < surveyData.questions.length
      ) {
        dispatch(setActiveStep(activeStep + 1));
      }

      if (type === SurveyButtonType.Prev && activeStep > 1) {
        dispatch(setActiveStep(activeStep - 1));
      }
    },
  },
});

export default surveyModule;
